import { useRef, useState, useCallback, ReactNode } from 'react';
import { cn } from '@/lib/core';

export interface MiniCardCarouselProps<T> {
  items: T[];
  /** Render a single carousel slide. */
  renderItem: (item: T, index: number) => ReactNode;
  /** Width of one card + gap in px — used to compute the active dot. Defaults to 252 (244 + 8). */
  cardStep?: number;
  /** Optional aria-label for the scrolling region. */
  ariaLabel?: string;
  className?: string;
}

/**
 * Generic horizontal-scroll carousel with dot pagination.
 * Mirrors the pattern from `components/shared/suggestions`.
 */
export function MiniCardCarousel<T>({
  items,
  renderItem,
  cardStep = 252,
  ariaLabel,
  className,
}: MiniCardCarouselProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    setActiveIndex(atEnd ? items.length - 1 : Math.round(el.scrollLeft / cardStep));
  }, [cardStep, items.length]);

  if (!items.length) return null;

  return (
    <div className={cn('flex flex-col gap-6 pb-4', className)}>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex w-[calc(100%+16px)] snap-x snap-mandatory scrollbar-none gap-2 overflow-x-auto"
        style={{ WebkitOverflowScrolling: 'touch' }}
        aria-label={ariaLabel}
      >
        {items.map((item, i) => (
          <div key={i} className="shrink-0 snap-start">
            {renderItem(item, i)}
          </div>
        ))}
        <div className="w-4 shrink-0" aria-hidden="true" />
      </div>

      {items.length > 1 && (
        <div className="flex items-center justify-center gap-2" aria-hidden="true">
          {items.map((_, i) => (
            <span
              key={i}
              className={cn(
                'block h-px w-4 transition-all duration-300',
                i === activeIndex ? 'bg-jncquoi-neutral-1000' : 'bg-jncquoi-neutral-300'
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
